/**
 * Centralized API client with unified error handling and token management
 * Follows Node.js best practices for centralized error handling
 */

import { AUTH_API_BASE_URL, CUSTOMER_API_BASE_URL, API_TIMEOUT } from '../config';
//import { isTokenExpired, clearAuthData } from '../utils/tokenValidation';

// Types for API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

// Custom error class for API errors
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly details?: any;
  public readonly isOperational: boolean = true;

  constructor(message: string, status: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    
    // Restore prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this);
  }
}

// Base API client class
class BaseApiClient {
  private baseURL: string;
  private timeout: number;

  constructor(baseURL: string, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL;
    this.timeout = timeout;
  }


  /**
 * Create request headers (no auth header needed, cookie used)
 */
private createHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

/**
 * Handle API response and errors (cookie-based auth)
 */
private async handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  let data: any;
  try {
    data = isJson ? await response.json() : await response.text();
  } catch (parseError) {
    throw new ApiClientError(
      'Failed to parse response',
      response.status,
      'PARSE_ERROR',
      { parseError, contentType }
    );
  }

  if (!response.ok) {
    const errorMessage = data?.message || data?.error || `HTTP ${response.status}`;
    const errorCode = data?.code || `HTTP_${response.status}`;

    // 🔒 Handle expired or invalid session (cookie-based)
    if (response.status === 401) {
      console.warn('[ApiClient] Session expired or unauthorized — redirecting to login.');
      // No token clearing needed — backend manages session cookies.
      window.location.href = '/login';
      return Promise.reject(
        new ApiClientError('Unauthorized - redirecting to login', 401, 'UNAUTHORIZED', data)
      );
    }

    throw new ApiClientError(errorMessage, response.status, errorCode, data);
  }

  return data;
}

 /**
 * Make HTTP request using cookie-based authentication
 */
async request<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${this.baseURL}${endpoint}`;
  const headers = this.createHeaders(); // No auth headers needed — cookies handle it

  const requestConfig: RequestInit = {
    ...options,
    credentials: 'include', // ✅ Send session cookies
    headers: {
      ...headers,
      ...options.headers,
    },
    signal: AbortSignal.timeout(this.timeout),
  };

  console.log('🌐 API Request:', {
    url,
    method: options.method || 'GET',
    headers: requestConfig.headers,
    body: options.body,
  });

  try {
    const response = await fetch(url, requestConfig);

    console.log('🌐 API Response:', {
      url,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
    });

    return await this.handleResponse<T>(response);
  } catch (error) {
    console.error('🌐 API Request Failed:', {
      url,
      error: error instanceof Error ? error.message : error,
    });

    // Handle network errors and timeouts
    if (error instanceof ApiClientError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiClientError(
        'Request timeout',
        408,
        'TIMEOUT_ERROR',
        { url, timeout: this.timeout }
      );
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiClientError(
        'Network error - please check your connection',
        0,
        'NETWORK_ERROR',
        { originalError: error.message }
      );
    }

    // Re-throw as standardized API error
    throw new ApiClientError(
      error instanceof Error ? error.message : 'Unknown error',
      500,
      'UNKNOWN_ERROR',
      error
    );
  }
}

  // HTTP method helpers (cookie-based, no includeAuth flag)
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

 async post<T = any>(endpoint: string, data?: any): Promise<T> {
  return this.request<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
  return this.request<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
  return this.request<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

  async delete<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Create API client instances
export const authApiClient = new BaseApiClient(AUTH_API_BASE_URL);
export const customerApiClient = new BaseApiClient(CUSTOMER_API_BASE_URL);

// Default export for the base client class
export default BaseApiClient; 